import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { INDUSTRIAS, SECTORES, TAMANOS, formatDateTime } from '../lib/constants';
import api from '../lib/api';
import { PLANS, PLAN_ORDER, FEATURE_MIN_PLAN, FEATURE_LABEL } from '../lib/plans';
import PlanBadge from '../components/PlanBadge';
import { Check, Lock, Sparkles, ArrowUpCircle } from 'lucide-react';
import { isValidRut, formatRut, formatRutInput } from '../lib/rut';
import { useQuotaUsage, pctOfQuota } from '../lib/useQuotaUsage';
import UpgradeRequestModal from '../components/UpgradeRequestModal';

export default function Settings() {
  const { user, organization, updateOrganization } = useAuth();
  const [form, setForm] = useState({
    razon_social: organization?.razon_social || '',
    rut: organization?.rut || '',
    giro: organization?.giro || '',
    industria: organization?.industria || 'otro',
    tamano: organization?.tamano || 'pequena',
    sector: organization?.sector || 'privado',
    website: organization?.website || '',
    logo_url: organization?.logo_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [changingPlan, setChangingPlan] = useState(null);
  const [rutError, setRutError] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { usage } = useQuotaUsage();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const validateRut = () => {
    if (!form.rut) { setRutError(null); return; }
    if (!isValidRut(form.rut)) {
      setRutError('RUT inválido. Verifique el número y el dígito verificador.');
    } else {
      setRutError(null);
      setForm((f) => ({ ...f, rut: formatRut(f.rut) }));
    }
  };

  const save = async () => {
    if (form.rut && !isValidRut(form.rut)) {
      setRutError('RUT inválido. Verifique el número y el dígito verificador.');
      toast.error('El RUT ingresado no es válido');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/organizations/me', form);
      updateOrganization(data);
      toast.success('Cambios guardados');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos guardar');
    } finally {
      setSaving(false);
    }
  };

  const changePlan = async (planKey) => {
    if (planKey === organization?.plan) return;
    setChangingPlan(planKey);
    try {
      const { data } = await api.post('/plan/upgrade', { plan: planKey });
      updateOrganization(data.organization);
      toast.success(`Plan cambiado a ${data.plan.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || err?.response?.data?.detail || 'No pudimos cambiar el plan');
    } finally {
      setChangingPlan(null);
    }
  };

  const currentPlan = PLANS[organization?.plan || 'pyme'];
  const isAdmin = user?.role === 'admin';

  // Features que la org tiene o no según su plan
  const featureRows = Object.entries(FEATURE_LABEL);

  return (
    <div className="max-w-4xl space-y-6" data-testid="settings-page">
      {/* ==== PLAN SECTION ==== */}
      <section id="plan" className="pv-card p-6 md:p-8" data-testid="settings-plan">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="pv-eyebrow">Plan y facturación</div>
            <h2 className="text-xl font-semibold text-[#1C2329] mt-1 flex items-center gap-3">
              Plan actual <PlanBadge planKey={currentPlan.key} />
            </h2>
            <p className="mt-2 text-[13px] text-[#6B7280]">
              {currentPlan.name} · {currentPlan.size} · <span className="tabular-nums">{currentPlan.price}</span>
            </p>
          </div>
        </div>

        {isAdmin && (
          <div className="mt-2 border-l-4 border-[#F59E0B] bg-[#FFFBEB] px-4 py-3 text-[12.5px] text-[#78350F]">
            <b>Modo demo:</b> puede cambiar de plan sin costo para probar los límites. Cuando activemos Stripe, el cambio se hará vía checkout con boleta electrónica SII.
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {PLAN_ORDER.map((key) => {
            const p = PLANS[key];
            const isCurrent = key === currentPlan.key;
            return (
              <div
                key={key}
                className={`p-4 border flex flex-col ${isCurrent ? 'border-[#2A9B68] bg-[#F0FBF5]' : 'border-[#E5E7EB] bg-white'}`}
                data-testid={`plan-tile-${key}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#6B7280]">{p.size}</span>
                  {isCurrent && <span className="text-[10px] px-1.5 py-0.5 bg-[#2A9B68] text-white uppercase tracking-wider font-semibold">Actual</span>}
                </div>
                <div className="mt-2 text-[16px] font-semibold text-[#1C2329]">{p.name}</div>
                <div className="mt-1 text-[13px] text-[#374151] tabular-nums">{p.price}</div>
                {isAdmin && !isCurrent && (
                  <button
                    onClick={() => changePlan(key)}
                    disabled={changingPlan !== null}
                    className="mt-4 w-full text-[12px] px-3 py-1.5 border border-[#D1D5DB] hover:border-[#143A34] hover:text-[#143A34] disabled:opacity-40"
                    data-testid={`plan-change-${key}`}
                  >
                    {changingPlan === key ? 'Cambiando…' : `Cambiar a ${p.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Feature matrix */}
        <div className="mt-8">
          <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B7280] mb-3">Qué incluye su plan</div>
          <div className="border border-[#E5E7EB]">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-[#FAFBFC] border-b border-[#E5E7EB]">
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Feature</th>
                  {PLAN_ORDER.map((k) => (
                    <th key={k} className="px-2 py-2 text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold text-center">
                      {PLANS[k].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {featureRows.map(([key, label]) => {
                  const minPlan = FEATURE_MIN_PLAN[key];
                  return (
                    <tr key={key} className="border-b border-[#E5E7EB] last:border-b-0">
                      <td className="px-4 py-2.5 text-[#1C2329]">{label}</td>
                      {PLAN_ORDER.map((planKey) => {
                        const included = PLANS[planKey].level >= PLANS[minPlan].level;
                        return (
                          <td key={planKey} className="px-2 py-2.5 text-center">
                            {included ? (
                              <Check size={14} className="inline text-[#2A9B68]" strokeWidth={2.5} />
                            ) : (
                              <Lock size={12} className="inline text-[#D1D5DB]" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Usage snapshot */}
        {usage && (
          <div className="mt-8" data-testid="settings-usage">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#6B7280]">Uso actual del plan</div>
              <button
                type="button"
                onClick={() => setShowUpgrade(true)}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#143A34] hover:text-[#0F2A26]"
                data-testid="usage-request-upgrade"
              >
                <ArrowUpCircle size={13} strokeWidth={2.2} />
                Solicitar aumento de plan
              </button>
            </div>
            <div className="border border-[#E5E7EB] bg-white divide-y divide-[#E5E7EB]">
              {Object.entries(usage.quotas).map(([key, row]) => {
                const pct = pctOfQuota(row);
                const unlimited = row.max === null || row.max === undefined;
                const critical = pct >= 100;
                const near = pct >= 80 && pct < 100;
                const barColor = critical ? '#DC2626' : near ? '#F59E0B' : '#2A9B68';
                return (
                  <div key={key} className="px-4 py-3" data-testid={`usage-row-${key}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-[13px] text-[#1C2329]">{row.label}</div>
                      <div className="text-[12.5px] text-[#374151] tabular-nums">
                        <b>{row.current}</b> {unlimited ? '· sin tope' : `/ ${row.max}`}
                      </div>
                    </div>
                    {!unlimited && (
                      <div className="mt-1.5 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all"
                          style={{ width: `${pct}%`, background: barColor }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <UpgradeRequestModal open={showUpgrade} onClose={() => setShowUpgrade(false)} />
      </section>

      {/* ==== ORG DATA SECTION (existente) ==== */}
      <section className="pv-card p-6 md:p-8">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="pv-eyebrow">Organización</div>
            <h2 className="text-xl font-semibold text-[#1C2329] mt-1">Datos de la empresa</h2>
          </div>
          <span className="text-[12px] text-[#6B7280]">
            Última actualización: {formatDateTime(organization?.updated_at)}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Razón social">
            <input className="pv-input" value={form.razon_social} onChange={set('razon_social')} data-testid="cfg-razon-social" />
          </F>
          <F label="RUT">
            <input
              className={`pv-input ${rutError ? 'border-[#DC2626] focus:border-[#DC2626]' : ''}`}
              value={form.rut}
              onChange={(e) => { setRutError(null); setForm((f) => ({ ...f, rut: formatRutInput(e.target.value) })); }}
              onBlur={validateRut}
              placeholder="12.345.678-9"
              data-testid="cfg-rut"
            />
            {rutError && (
              <div className="text-[12px] text-[#991B1B] mt-1" data-testid="cfg-rut-error">{rutError}</div>
            )}
          </F>
          <F label="Giro">
            <input className="pv-input" value={form.giro} onChange={set('giro')} data-testid="cfg-giro" />
          </F>
          <F label="Sitio web">
            <input className="pv-input" value={form.website} onChange={set('website')} data-testid="cfg-website" />
          </F>
          <F label="URL del logo (opcional)">
            <input className="pv-input" value={form.logo_url} onChange={set('logo_url')}
              placeholder="https://miempresa.cl/logo.png" data-testid="cfg-logo-url" />
            {form.logo_url && (
              <div className="mt-2 border border-[#E5E7EB] bg-[#FAFBFC] p-2 inline-block">
                <img src={form.logo_url} alt="preview" className="h-10 max-w-[180px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
            )}
          </F>
          <F label="Industria">
            <select className="pv-input" value={form.industria} onChange={set('industria')} data-testid="cfg-industria">
              {INDUSTRIAS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </F>
          <F label="Tamaño">
            <select className="pv-input" value={form.tamano} onChange={set('tamano')} data-testid="cfg-tamano">
              {TAMANOS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </F>
          <F label="Sector">
            <select className="pv-input" value={form.sector} onChange={set('sector')} data-testid="cfg-sector">
              {SECTORES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </F>
        </div>

        <div className="mt-8 flex justify-end">
          <button className="pv-btn-primary" onClick={save} disabled={saving} data-testid="cfg-save">
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </section>

      <section className="pv-card p-6 md:p-8">
        <div className="pv-eyebrow">Su cuenta</div>
        <h2 className="text-xl font-semibold text-[#1C2329] mt-1 mb-6">Perfil de usuario</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <Row k="Nombre" v={`${user?.nombre} ${user?.apellido}`} />
          <Row k="Correo" v={user?.email} />
          <Row k="Rol" v={user?.role} />
          <Row k="Cargo" v={user?.cargo || '—'} />
        </div>
        <div className="mt-6 text-[12px] text-[#6B7280]">
          Los cambios de contraseña estarán disponibles en fases posteriores.
        </div>
      </section>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between p-3 border border-[#E5E7EB] rounded-md">
      <span className="text-[12px] uppercase tracking-wider text-[#6B7280]">{k}</span>
      <span className="text-[14px] text-[#1C2329] font-medium">{v}</span>
    </div>
  );
}
